import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import { Button, Text, theme, styles as baseStyles } from '@socrait/react';
import FlashMessage from 'react-native-flash-message';

import { sessionStore } from '~/store/sessionStore';
import { Loading } from '~/components/Loading';
import { FlashBanner } from '~/components/FlashBanner';
import { SMALL_SCREEN_WIDTH } from '~/constants';
import { LayoutWithSidebar } from '~/layouts/LayoutWithSidebar';
import {
  usePeriodGetByIdQuery,
  usePeriodEditMutation,
  usePeriodAddMutation,
  usePeriodDeleteMutation,
  usePeriodArchiveMutation,
} from '~/services/period';
import { trackEvent } from '~/gtag';
import { showError } from '~/utils/showError';

import { ArchiveModal } from './ArchiveModal';
import { DeleteModal } from './DeleteModal';
import { DescriptionColumn } from './DescriptionColumn';
import { EditForm } from './EditForm';
import { SaveChangesModal } from './SaveChangesModal';

type ClassAddEditProps = {
  periodId?: string;
  operation: 'add' | 'edit';
  isSetup?: boolean;
  existingShortNames?: string[];
  numPeriods?: number;
  onClose: (flashMessage?: string, type?: string) => void;
  onSubmit: (data?: unknown) => void;
};

export function ClassAddEditScreen({
  periodId = '',
  operation,
  isSetup = false,
  existingShortNames = [],
  numPeriods,
  onClose,
  onSubmit,
}: ClassAddEditProps) {
  const {
    data: periodData,
    status: periodStatus,
    error: periodError,
  } = usePeriodGetByIdQuery({
    periodId,
    includeStudents: true,
  });
  const classSetupData = sessionStore((store) => store.classSetupData);
  const setClassSetupData = sessionStore((store) => store.setClassSetupData);
  const studentsData = periodData?.period?.students;

  const [shortName, setShortName] = useState(
    isSetup ? (classSetupData?.shortName ?? '') : (periodData?.period?.shortName ?? ''),
  );
  const [className, setClassName] = useState(
    isSetup ? (classSetupData?.className ?? '') : (periodData?.period?.name ?? ''),
  );
  const [inputVoiceName, setInputVoiceName] = useState('');
  const [studentList, setStudentList] = useState<{ id: string; voiceName: string }[]>(
    isSetup
      ? (classSetupData?.studentList ?? [])
      : (studentsData?.map((s: { id: string; voiceName: string }) => ({
          id: s.id,
          voiceName: s.voiceName,
        })) ?? []),
  );
  const [duplicateName, setDuplicateName] = useState<string | undefined>(undefined);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [userHasEdited, setUserHasEdited] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const notificationRef = useRef<FlashMessage>(null);

  const { mutate: editPeriod, status: saveStatus } = usePeriodEditMutation();
  const { mutate: addPeriod } = usePeriodAddMutation();
  const { mutate: deletePeriod } = usePeriodDeleteMutation();
  const { mutate: archivePeriod, status: archiveStatus } = usePeriodArchiveMutation();

  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < SMALL_SCREEN_WIDTH;

  useEffect(() => {
    if (operation === 'edit') {
      trackEvent('roster_view');
    }
  }, [operation]);

  // Update state when query results change
  useEffect(() => {
    if (periodData?.period?.name) {
      setClassName(periodData.period.name);
    }
    if (periodData?.period?.shortName) {
      setShortName(periodData.period.shortName);
    }
  }, [periodData]);

  useEffect(() => {
    if (studentsData?.length) {
      const sortedStudents = [...studentsData].sort((a, b) => a.voiceName.localeCompare(b.voiceName));
      setStudentList(
        sortedStudents.map((student: { id: string; voiceName: string }) => ({
          id: student.id,
          voiceName: student.voiceName,
        })),
      );
    }
  }, [studentsData]);

  const handleShortNameChange = useCallback(
    (text: string) => {
      if (!userHasEdited) {
        setUserHasEdited(true);
      }
      setShortName(text);
    },
    [userHasEdited],
  );

  const handleClassNameChange = useCallback(
    (text: string) => {
      if (!userHasEdited) {
        setUserHasEdited(true);
      }
      setClassName(text);
    },
    [userHasEdited],
  );

  const handleOnChangeVoiceNameText = useCallback(
    (text: string) => {
      if (duplicateName) {
        setDuplicateName(undefined);
      }

      setInputVoiceName(text);
    },
    [duplicateName],
  );

  const handleVoiceNameEnter = useCallback(() => {
    const trimmedInputValue = inputVoiceName.trim();

    if (!trimmedInputValue.length) {
      return;
    }

    const isDuplicate = studentList.find((item) => item.voiceName.toLowerCase() === trimmedInputValue.toLowerCase());

    if (!isDuplicate) {
      if (!userHasEdited) {
        setUserHasEdited(true);
      }

      const tempId = `temp-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
      const newList = [{ id: tempId, voiceName: trimmedInputValue }, ...studentList];
      setStudentList(newList);
      setInputVoiceName('');
    } else {
      setDuplicateName(trimmedInputValue);
    }
  }, [inputVoiceName, studentList, userHasEdited]);

  const handleStudentListChange = useCallback(
    (students: { id: string; voiceName: string }[]) => {
      if (!userHasEdited) {
        setUserHasEdited(true);
      }
      setStudentList(students);
    },
    [userHasEdited],
  );

  const transformStudentList = (students: { id: string; voiceName: string }[]) =>
    students.map(({ id, voiceName }) => (id.startsWith('temp-') ? { voiceName } : { id, voiceName }));

  const handleClose = useCallback(
    (flashMessage?: string, type?: string) => {
      setUserHasEdited(false);

      if (isSetup) {
        setClassSetupData({ shortName, className, studentList });
      }

      onClose(flashMessage, type);
    },
    [isSetup, shortName, className, studentList, onClose, setClassSetupData],
  );

  const handleError = useCallback(
    (error: Error, type: 'archive' | 'delete', setModalOpen: (modalOpen: boolean) => void) => {
      setModalOpen(false);
      showError(
        error,
        `Failed to ${type} class. Please try again, or contact support if the issue persists.`,
        notificationRef,
      );
    },
    [],
  );

  const onSaveClass = useCallback(() => {
    if (isSaveChangesModalOpen) {
      setIsSaveChangesModalOpen(false);
    }

    const newStudentList = transformStudentList(studentList);

    if (operation === 'add') {
      addPeriod(
        {
          shortName,
          name: className,
          voiceNames: newStudentList.map(({ voiceName }) => voiceName),
        },
        {
          onSuccess: (period) => {
            trackEvent('class_add');
            if (isSetup) {
              setClassSetupData(undefined);
            }
            onSubmit(period);
          },
          onError: (error) => {
            showError(
              error,
              'Failed to add class. Please try again, or contact support if the issue persists.',
              notificationRef,
            );
          },
        },
      );
    } else {
      editPeriod(
        {
          periodId: periodId,
          shortName: shortName.trim(),
          name: className,
          students: newStudentList,
        },
        {
          onSuccess: () => {
            trackEvent('class_edit');
            trackEvent('roster_edit');
            onSubmit();
          },
          onError: (error) => {
            showError(
              error,
              'Failed to edit class. Please try again, or contact support if the issue persists.',
              notificationRef,
            );
          },
        },
      );
    }
  }, [
    periodId,
    shortName,
    className,
    studentList,
    editPeriod,
    addPeriod,
    onSubmit,
    isSetup,
    setClassSetupData,
    isSaveChangesModalOpen,
    operation,
  ]);

  const onDeleteClass = useCallback(() => {
    deletePeriod(
      { periodId },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          handleClose('Class successfully deleted', 'success');
        },
        onError: (error) => {
          handleError(error, 'delete', setIsDeleteModalOpen);
        },
      },
    );
  }, [periodId, deletePeriod, handleClose, handleError]);

  const onArchiveClass = useCallback(() => {
    archivePeriod(
      { periodId },
      {
        onSuccess: () => {
          handleClose('Class successfully archived', 'success');
        },
        onError: (error) => {
          handleError(error, 'archive', setIsArchiveModalOpen);
        },
      },
    );
  }, [periodId, archivePeriod, handleClose, handleError]);

  const onClosePress = useCallback(() => {
    if (userHasEdited) {
      setIsSaveChangesModalOpen(true);
    } else {
      handleClose();
    }
  }, [handleClose, userHasEdited]);

  const shortNameError = useMemo(() => {
    // When editing, exclude the current period's shortName from validation
    const shortNamesToCheck =
      operation === 'edit' && periodData?.period?.shortName
        ? existingShortNames.filter((n) => n.toLowerCase() !== periodData.period.shortName.toLowerCase())
        : existingShortNames;

    if (shortNamesToCheck.some((n) => n.toLocaleLowerCase() === shortName.toLocaleLowerCase())) {
      return 'Period number already exists. Try another number.';
    }
    return undefined;
  }, [shortName, existingShortNames, operation, periodData?.period?.shortName]);

  const hasShortNameError = !!shortNameError;

  const canSaveForm =
    !!shortName.length && !!className.length && !!studentList.length && saveStatus !== 'pending' && !hasShortNameError;

  const isLoading = operation === 'edit' && periodStatus === 'pending';

  return (
    <>
      <LayoutWithSidebar isSimple={true}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          <View style={[styles.editForm, isSmallScreen && styles.editFormSmall]}>
            <View style={styles.header} id="header">
              <Text style={styles.headerText}>{operation === 'add' ? 'Add' : 'Edit'} Class</Text>
              {!isSetup ? <Button icon="close" onPress={onClosePress} containerStyle={styles.closeButton} /> : null}
            </View>
            {isLoading ? (
              <Loading backgroundColor={theme.colors.white} />
            ) : (
              <EditForm
                shortName={shortName}
                className={className}
                inputVoiceName={inputVoiceName}
                studentList={studentList}
                operation={operation}
                canSaveForm={canSaveForm}
                setShortName={handleShortNameChange}
                setClassName={handleClassNameChange}
                handleOnChangeVoiceNameText={handleOnChangeVoiceNameText}
                handleVoiceNameEnter={handleVoiceNameEnter}
                setStudentList={handleStudentListChange}
                onSaveClass={onSaveClass}
                setIsDeleteModalOpen={setIsDeleteModalOpen}
                setIsArchiveModalOpen={setIsArchiveModalOpen}
                isSetup={isSetup}
                duplicateName={duplicateName}
                shortNameError={shortNameError}
                periodError={periodError}
                numPeriods={numPeriods}
                onBackPress={handleClose}
                isArchivePending={archiveStatus === 'pending'}
              />
            )}
          </View>
          <DescriptionColumn />
        </View>
        {operation === 'edit' && !!periodData?.period ? (
          <>
            <DeleteModal
              period={periodData?.period}
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              onConfirm={onDeleteClass}
            />
            <ArchiveModal
              isOpen={isArchiveModalOpen}
              isArchivePending={archiveStatus === 'pending'}
              onClose={() => setIsArchiveModalOpen(false)}
              onConfirm={onArchiveClass}
            />
          </>
        ) : null}
        <SaveChangesModal
          isOpen={isSaveChangesModalOpen}
          onCloseModal={() => setIsSaveChangesModalOpen(false)}
          onCloseEdit={handleClose}
          onSave={onSaveClass}
        />
      </LayoutWithSidebar>
      <FlashMessage
        ref={notificationRef}
        position="top"
        animationDuration={100}
        duration={10000}
        MessageComponent={({ message }: { message: { message: string; type: 'warning' | 'success' } }) => (
          <FlashBanner message={message} />
        )}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    maxHeight: '100%',
    justifyContent: 'center',
    position: 'relative',
    paddingRight: 220,
  },
  containerSmall: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    paddingRight: 0,
  },
  closeButton: {
    borderWidth: 0,
  },
  editForm: {
    flex: 1,
    maxWidth: 1500,
    gap: theme.spacing.xlarge,
  },
  editFormSmall: {
    maxWidth: '100%',
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing.xlarge * 2,
    paddingRight: theme.spacing.xlarge * 2 - theme.spacing.large,
    paddingTop: theme.spacing.xlarge * 2,
  },
  headerText: {
    ...baseStyles.boldText,
    ...baseStyles.largeText,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});
